// Recycling location rows instead of inserting new ones.
//
// A third of the table is dead weight: of 121 locations, 38 have no rooms and
// no devices, and 27 of those already sit at MasterUserId 0. So a new site
// claims a free row and only inserts when the pool is empty.
//
// ── FREE MEANS EMPTY, NEVER "UNOWNED" ──────────────────────────────────────
//
// ⚠️ THE TWO ARE NOT THE SAME, and treating them as the same hands a live site
// to a stranger. FIVE locations on the live table have MasterUserId 0 while
// still holding rooms and devices — id 255 has EIGHT devices. Testing ownership
// alone would list every one of them as free.
//
// So `isFree` asks the only question that matters — does this location contain
// anything — and ownership is a secondary marker, not the test.
//
// ── CLAIMING IS A SCRUB, NOT A FLAG FLIP ───────────────────────────────────
//
// ⚠️ 28 OF THE 38 EMPTY ROWS STILL CARRY A PREVIOUS CUSTOMER'S STREET ADDRESS.
// Handing one over by setting MasterUserId and LocationName alone gives the new
// owner somebody else's ADD1/CITY/PINCODE/MOBILE1 — and, the first time an
// emptied row has one, their Wi-Fi password. Every field a customer can see or
// that identifies a site is blanked on claim; nothing is inherited.
//
// ⚠️ AND THE REFERENCES HAVE TO GO TOO. "No rooms and no devices" does not mean
// "nothing points here": `GetLocationsByUserId` joins UserLocationMapping, so a
// stale mapping keeps the OLD customer seeing a location that now belongs to
// somebody else. That is not hypothetical — it is exactly the phantom that made
// unallot look broken for Bharat: location 11 has no rooms and no devices, and
// still appears in a user's list.
//
// ── Releasing is an EXPLICIT act ───────────────────────────────────────────
//
// ⚠️ NOTHING HERE RUNS AUTOMATICALLY WHEN THE LAST DEVICE LEAVES. A location is
// briefly empty in the middle of every migration — the merge that moved 24
// devices emptied one for several seconds — and an automatic sweeper would
// recycle a live site out from under the person moving it. Release is called by
// an operator who has seen the row, or not at all.

import {
  getAllLocations, getAllRoomsAtLocation, getDevicesByLocation, updateLocation,
  addLocation, addUserLocationMapping,
  deleteAllUserLocationMappings, deleteAllLocationRoomsMappings,
} from '../api/api';

const rows = (res) => (res && Array.isArray(res.data) ? res.data : []);
const num = (v) => Number(v) || 0;

/**
 * Every field that identifies a site, blanked.
 *
 * ⚠️ NOT A SUBSET. Anything left holding a value is inherited by the next
 * owner, and the fields that matter most (address, mobile, Wi-Fi password) are
 * exactly the ones nobody thinks to check on a row that reads as "empty".
 * `IsActive` is set here too: a claimed row must be live, a released one must
 * not be.
 */
export const BLANK_LOCATION = {
  LocationName: '',
  ADD1: '',
  ADD2: '',
  CITY: '',
  PINCODE: '',
  MOBILE1: '',
  MOBILE2: '',
  WifiName: '',
  WifiPassword: '',
  Password: '',
  GlobalIp: '',
  RouterIP: '',
  NodeIP: '',
  MqttDevice: '',
  MqttClient: '',
  Longitude: '',
  Latitude: '',
  ImageId: 0,
  ImageFileName: '',
  SortOrder: 0,
  LocationLock: 0,
};

/**
 * Does this location contain anything?
 *
 * -> { empty, rooms, devices } — counts included so a caller can say WHY it
 *    refused, rather than only that it did.
 *
 * ⚠️ A FAILED READ IS NOT AN EMPTY LOCATION. If either request fails this
 * reports `empty: false`: the alternative is that a timeout makes an occupied
 * site look free, and the next claim scrubs a customer's address.
 */
export async function locationContents(locationId) {
  const id = num(locationId);
  if (!id) return { empty: false, rooms: null, devices: null };
  try {
    const [rms, devs] = await Promise.all([
      getAllRoomsAtLocation(id).then(rows),
      getDevicesByLocation(id).then(rows),
    ]);
    return { empty: rms.length === 0 && devs.length === 0, rooms: rms.length, devices: devs.length };
  } catch (e) {
    return { empty: false, rooms: null, devices: null };
  }
}

/**
 * Locations that can be handed out. -> [{ LocationID, LocationName, … }]
 *
 * Emptiness is checked per row, so this is one request per location. Called
 * only when a location is being created, which is rare, and the alternative is
 * trusting `MasterUserId = 0` — which five live rows would fail.
 */
export async function findFreeLocations(limit = 1) {
  const all = rows(await getAllLocations().catch(() => null));
  // Unowned rows first: they are the ones most likely to be genuinely spare, so
  // the usual case checks one location rather than all 121.
  const candidates = all
    .filter((l) => num(l.MasterUserId) === 0)
    .sort((a, b) => num(a.LocationID) - num(b.LocationID));
  const free = [];
  for (let i = 0; i < candidates.length && free.length < limit; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    const { empty } = await locationContents(candidates[i].LocationID);
    if (empty) free.push(candidates[i]);
  }
  return free;
}

/**
 * Take a free row and give it to a user. -> LocationID, or null if not free.
 *
 * The emptiness check is repeated here rather than trusted from the caller:
 * between listing and claiming, an installer may have assigned a device to it.
 */
export async function claimLocation(locationId, {
  userId, locationName, address,
}) {
  const id = num(locationId);
  const { empty } = await locationContents(id);
  if (!empty) return null;

  const a = address || {};
  const text = (v) => String(v ?? '').trim();
  await updateLocation(id, {
    ...BLANK_LOCATION,
    IsActive: 1,
    MasterUserId: num(userId),
    LocationName: text(locationName),
    ADD1: text(a.add1),
    ADD2: text(a.add2),
    CITY: text(a.city),
    PINCODE: text(a.pincode),
  });
  // Nobody who used to see it may keep seeing it.
  await deleteAllUserLocationMappings(id).catch(() => {});
  await deleteAllLocationRoomsMappings(id).catch(() => {});
  await addUserLocationMapping(userId, id).catch(() => {});
  return id;
}

/**
 * Put a location back in the pool. -> { ok, reason }
 *
 * ⚠️ REFUSES A LOCATION THAT STILL HOLDS ANYTHING, and says what it holds. This
 * is the guard that stops a mistyped id blanking a live site's address.
 */
export async function releaseLocation(locationId) {
  const id = num(locationId);
  if (!id) return { ok: false, reason: 'No location id.' };
  const { empty, rooms, devices } = await locationContents(id);
  if (rooms == null) return { ok: false, reason: 'Could not read the location — nothing changed.' };
  if (!empty) {
    return {
      ok: false,
      reason: `Still holds ${rooms} room${rooms === 1 ? '' : 's'} and ${devices} device${devices === 1 ? '' : 's'}.`,
    };
  }
  await updateLocation(id, { ...BLANK_LOCATION, IsActive: 0, MasterUserId: 0 });
  await deleteAllUserLocationMappings(id).catch(() => {});
  await deleteAllLocationRoomsMappings(id).catch(() => {});
  return { ok: true, reason: '' };
}

/**
 * Create a location — from the pool when one is free, else a fresh insert.
 *
 * -> { locationId, recycled }
 *
 * ⚠️ THE INSERT IS THE FALLBACK, NOT THE FAILURE PATH. If the pool is empty, or
 * a claim loses a race to another admin, this must still produce a location:
 * refusing to create one because no row was spare would be a worse outcome than
 * the clutter recycling exists to avoid.
 */
export async function createOrClaimLocation(userId, locationName, address) {
  const [free] = await findFreeLocations(1);
  if (free) {
    const claimed = await claimLocation(free.LocationID, { userId, locationName, address });
    if (claimed) return { locationId: claimed, recycled: true };
  }
  const a = address || {};
  const text = (v) => String(v ?? '').trim();
  const res = await addLocation({
    LocationName: locationName,
    MasterUserId: num(userId),
    ADD1: text(a.add1),
    ADD2: text(a.add2),
    CITY: text(a.city),
    PINCODE: text(a.pincode),
  });
  const id = res?.data?.insertId ?? res?.data?.[0]?.insertId;
  if (id == null) throw new Error('The server did not return the new location id.');
  await addUserLocationMapping(userId, id).catch(() => {});
  return { locationId: id, recycled: false };
}
